// Chart colours and ECharts option builders for Business Analytics.
//
// Series colours come from the PayAngel logo, which has two colours: green (#6EB644) and navy
// (#212F3F). Five lines need five colours, so slots 3–5 are darker and lighter shades of those two:
// green, navy, dark green, slate, pale green. They're assigned by fixed slot and never cycled, so a
// series keeps its colour on every chart. The light set passes a colour-blind check on every pair
// (worst CVD ΔE 14.9). Pale green and green sit below 3:1 contrast on white, which is why every
// chart has a legend and a table view.

/** PayAngel logo colours. */
export const BRAND = {
  green: "#6EB644",
  navy: "#212F3F",
};

/** Failed is always red, whatever slot it's in, so failures read as failures. */
export const FAILED_RED = "#E34948";

const LIGHT = {
  series: [BRAND.green, BRAND.navy, "#2F5E1B", "#8494A6", "#C3E3AD"],
  textPrimary: "#0b0b0b",
  textSecondary: "#52514e",
  grid: "#e6e5e0",
  axis: "#c9c8c2",
  band: "rgba(82, 81, 78, 0.08)",
  tooltipBg: "#ffffff",
};

// Navy disappears on a dark background, so dark mode uses lighter steps of the same two colours.
// Pale green and pale navy are close when all five lines show; the legend and table cover that.
const DARK = {
  series: ["#7CC653", "#D6DEE8", "#4B8A2C", "#7888A0", "#B4DC9A"],
  textPrimary: "#ffffff",
  textSecondary: "#c3c2b7",
  grid: "#3a3a37",
  axis: "#55544f",
  band: "rgba(195, 194, 183, 0.10)",
  tooltipBg: "#26261f",
};

/** The admin app switches to its dark theme by putting `full-dark` on <body>. */
export function isDarkTheme() {
  return typeof document !== "undefined" && document.body.classList.contains("full-dark");
}

export function palette(dark = isDarkTheme()) {
  return dark ? DARK : LIGHT;
}

/** A series' fixed colour, swapped for its dark-mode step when a logo colour would disappear. */
function seriesColor(color, colors) {
  if (colors === DARK && color === BRAND.green) return DARK.series[0];
  if (colors === DARK && color === BRAND.navy) return DARK.series[1];
  return color;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09" → "Sep 2026" */
export function monthLabel(month) {
  const [year, m] = month.split("-");
  return `${MONTH_NAMES[Number(m) - 1]} ${year}`;
}

export function formatCount(value) {
  return value == null ? "–" : Number(value).toLocaleString("en-GB");
}

export function formatMoney(value, currency) {
  if (value == null) return "–";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
  } catch (e) {
    // Unknown or non-ISO currency code: fall back to a plain number with the code.
    return `${Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })} ${currency}`;
  }
}

/** Compact axis labels: 1.2k, 3.4M */
export function compact(value) {
  return new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

/** Tooltip swatch for a series, matching ECharts' own marker. */
function marker(color) {
  return `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:9px;height:9px;background-color:${color};"></span>`;
}

/**
 * Background columns behind flagged months (e.g. "Approx."), drawn as full-width bars on a hidden
 * 0–1 axis so a single flagged month still gets a visible band. The label sits on the first month
 * of each run.
 */
function bandSeries(flags, label, colors) {
  return {
    name: "__band",
    type: "bar",
    yAxisIndex: 1,
    silent: true,
    z: 0,
    barWidth: "100%",
    itemStyle: { color: colors.band },
    data: flags.map((on, i) =>
      on
        ? {
            value: 1,
            label: {
              show: i === 0 || !flags[i - 1],
              position: "insideTop",
              formatter: label,
              color: colors.textSecondary,
              fontSize: 11,
            },
          }
        : null
    ),
  };
}

/** A tidy tick step near `raw`: 1, 2, 2.5 or 5 × a power of ten. */
function niceStep(raw) {
  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * power >= raw);
  return step * power;
}

/**
 * Axis bounds that start near the data instead of at zero, for totals that never drop near zero.
 * Pads by a quarter of the data's range (at least 5% of the top value), so a small wobble doesn't fill the whole height and
 * read as a swing, then rounds to tidy ticks (about four of them) so the labels are round numbers.
 */
function fittedAxis(values, integer) {
  const present = values.filter((v) => v != null);
  if (!present.length) return {};
  const lo = Math.min(...present);
  const hi = Math.max(...present);
  const pad = Math.max((hi - lo) / 4, hi * 0.05, integer ? 1 : 0);
  let step = niceStep(Math.max((hi - lo + 2 * pad) / 4, integer ? 1 : Number.EPSILON));
  if (integer) step = Math.max(1, Math.round(step));
  return {
    min: Math.max(0, Math.floor((lo - pad) / step) * step),
    max: Math.ceil((hi + pad) / step) * step,
    interval: step,
  };
}

/**
 * A line chart over Reporting Months.
 *
 * The current (partial) month is drawn as a dashed continuation of each line, so a month that
 * has barely started doesn't read as a collapse. The axis always fits every value.
 *
 * @param {Object} opts
 * @param {Array<{ month: string, partial: boolean }>} opts.months
 * @param {Array<{ name: string, data: Array<number|null>, emphasis?: boolean, color?: string }>} opts.series
 *   Each series takes the colour of its position in this list, unless it names its own `color`.
 * @param {(value: number) => string} opts.format  tooltip value format
 * @param {boolean[]} [opts.approximate]  months to shade as approximate
 * @param {string} [opts.approximateLabel]
 * @param {boolean} [opts.integer]  whole-number axis (counts)
 * @param {boolean} [opts.fitToData]  start the axis near the lowest value instead of at zero, for
 *   totals like a business count that never drop near zero
 */
export function lineChartOption({
  months,
  series,
  format,
  approximate,
  approximateLabel = "Approx.",
  integer = false,
  fitToData = false,
}) {
  const colors = palette();
  const categories = months.map((m) => monthLabel(m.month));
  const partial = months.map((m) => m.partial);
  // The dashed segment runs from the last full month into the partial month.
  const inDashed = partial.map((p, i) => p || Boolean(partial[i + 1]));

  const echartsSeries = [];
  series.forEach((s, i) => {
    const color = s.color ? seriesColor(s.color, colors) : colors.series[i];
    const width = s.emphasis ? 3 : 2;
    const base = {
      type: "line",
      z: 2,
      connectNulls: false,
      symbol: "circle",
      symbolSize: 8,
      itemStyle: { color, borderColor: colors.tooltipBg, borderWidth: 2 },
      emphasis: { focus: "series" },
    };
    echartsSeries.push({
      ...base,
      name: s.name,
      data: s.data.map((v, j) => (partial[j] ? null : v)),
      lineStyle: { width },
    });
    if (partial.some(Boolean)) {
      echartsSeries.push({
        ...base,
        name: `${s.name} (so far)`,
        // The last full month keeps its solid marker; only the partial month gets a hollow one.
        data: s.data.map((v, j) => (!inDashed[j] ? null : partial[j] ? v : { value: v, symbol: "none" })),
        lineStyle: { width, color, type: "dashed" },
        itemStyle: { color: colors.tooltipBg, borderColor: color, borderWidth: 2 },
      });
    }
  });
  if (approximate && approximate.some(Boolean)) {
    echartsSeries.push(bandSeries(approximate, approximateLabel, colors));
  }

  return {
    animation: false,
    textStyle: { color: colors.textPrimary, fontFamily: "inherit" },
    grid: { left: 8, right: 16, top: series.length > 1 ? 40 : 16, bottom: 8, containLabel: true },
    legend:
      series.length > 1
        ? {
            data: series.map((s) => s.name),
            // One scrollable row, so a wrapping legend never runs into the plot on narrow screens.
            type: "scroll",
            top: 0,
            left: 0,
            icon: "roundRect",
            itemWidth: 12,
            itemHeight: 4,
            textStyle: { color: colors.textSecondary },
            pageTextStyle: { color: colors.textSecondary },
          }
        : undefined,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line", lineStyle: { color: colors.axis } },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.grid,
      textStyle: { color: colors.textPrimary },
      formatter(params) {
        const i = params[0].dataIndex;
        const notes = [];
        if (partial[i]) notes.push("so far");
        if (approximate && approximate[i]) notes.push(approximateLabel.toLowerCase());
        const title = `<strong>${categories[i]}</strong>${notes.length ? ` (${notes.join(", ")})` : ""}`;
        const rows = series.map((s, k) => {
          const v = s.data[i];
          return `${marker(s.color ? seriesColor(s.color, colors) : colors.series[k])}${s.name}: <strong>${v == null ? "–" : format(v)}</strong>`;
        });
        return [title].concat(rows).join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: true,
      axisLine: { lineStyle: { color: colors.axis } },
      axisTick: { show: false },
      axisLabel: { color: colors.textSecondary, hideOverlap: true },
    },
    yAxis: [
      {
        type: "value",
        minInterval: integer ? 1 : undefined,
        ...(fitToData ? fittedAxis(series.flatMap((x) => x.data), integer) : {}),
        splitLine: { lineStyle: { color: colors.grid } },
        axisLabel: { color: colors.textSecondary, formatter: compact },
      },
      // Hidden 0–1 axis for the band columns.
      { type: "value", min: 0, max: 1, show: false },
    ],
    series: echartsSeries,
  };
}


/**
 * Business Profiles scatter: one marker per company, Collector (x = 0) to Disburser (x = 100), money
 * moved per month on a log y axis (companies range from hundreds to millions). Each marker is a small
 * chip showing the company's country code, so clusters by country stand out; chips are coloured by
 * Direction, which has its own legend.
 *
 * @param {Object} opts
 * @param {Array<{ name, direction, payers, payees, x, y }>} opts.points
 * @param {(value: number) => string} opts.format  money format for the tooltip
 * @param {(value: number) => string} opts.axisFormat  short money format for the y axis
 */
export function profileScatterOption({ points, format, axisFormat }) {
  const colors = palette();
  const directions = [
    { name: "Collector", color: colors.series[0] },
    { name: "Both", color: colors.series[3] },
    { name: "Disburser", color: colors.series[1] },
  ];
  const ends = { 0: "Collector", 50: "Even", 100: "Disburser" };
  return {
    animation: false,
    textStyle: { color: colors.textPrimary, fontFamily: "inherit" },
    grid: { left: 8, right: 24, top: 40, bottom: 28, containLabel: true },
    legend: {
      data: directions.map((d) => d.name),
      top: 0,
      left: 0,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: colors.textSecondary },
    },
    tooltip: {
      trigger: "item",
      backgroundColor: colors.tooltipBg,
      borderColor: colors.grid,
      textStyle: { color: colors.textPrimary },
      formatter({ data, color }) {
        const p = data.point;
        return [
          `${marker(color)}<strong>${p.name}</strong> (${p.direction})`,
          p.country ? `Country: <strong>${p.country}</strong>` : "",
          `Payers: <strong>${formatCount(p.payers)}</strong> · Payees: <strong>${formatCount(p.payees)}</strong>`,
          `Moved per month: <strong>${format(p.y)}</strong>`,
        ]
          .filter(Boolean)
          .join("<br/>");
      },
    },
    xAxis: {
      type: "value",
      min: 0,
      max: 100,
      interval: 25,
      name: "Share of counterparties that are payees",
      nameLocation: "middle",
      nameGap: 28,
      nameTextStyle: { color: colors.textSecondary },
      splitLine: { lineStyle: { color: colors.grid } },
      axisLine: { lineStyle: { color: colors.axis } },
      axisLabel: { color: colors.textSecondary, formatter: (v) => ends[v] || `${v}%` },
    },
    yAxis: {
      type: "log",
      logBase: 10,
      splitLine: { lineStyle: { color: colors.grid } },
      axisLabel: { color: colors.textSecondary, formatter: axisFormat },
    },
    series: directions.map((d) => ({
      name: d.name,
      type: "scatter",
      symbol: "roundRect",
      symbolSize: [24, 16],
      itemStyle: { color: d.color, opacity: 0.85, borderColor: colors.tooltipBg, borderWidth: 1 },
      label: {
        show: true,
        position: "inside",
        formatter: ({ data }) => data.point.countryCode,
        color: "#ffffff",
        fontSize: 10,
        fontWeight: 600,
      },
      emphasis: { focus: "series" },
      data: points.filter((p) => p.direction === d.name).map((p) => ({ value: [p.x, p.y], point: p })),
    })),
  };
}
